import { useState } from 'react'
import type { HintInfo } from '@/lib/api'

type Props = { hints: HintInfo[]; onReveal: (hintId: string) => Promise<void> | void; disabled: boolean }

/**
 * Revealed hints stay visible. The next unrevealed hint needs an explicit
 * confirmation. Penalties are recorded server-side but not surfaced to players.
 */
export function HintPanel({ hints, onReveal, disabled }: Props) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  if (hints.length === 0) return null

  const revealed = hints.filter((h) => h.revealed)
  const next = hints.find((h) => !h.revealed)

  async function reveal(id: string) {
    setBusy(true)
    try {
      await onReveal(id)
      setConfirming(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="hints" aria-label="Hints">
      {revealed.length > 0 && (
        <ol className="hint-list">
          {revealed.map((h, i) => (
            <li key={h.id}>
              <span className="muted">Hint {i + 1}</span> {h.text}
            </li>
          ))}
        </ol>
      )}
      {next &&
        (confirming ? (
          <div className="hint-confirm" role="group" aria-label="Confirm hint">
            <p>
              Reveal hint {revealed.length + 1} of {hints.length}? Hints you use are recorded with your result.
            </p>
            <div className="button-row">
              <button type="button" onClick={() => setConfirming(false)} disabled={busy}>
                Not now
              </button>
              <button type="button" className="primary" onClick={() => void reveal(next.id)} disabled={busy || disabled}>
                {busy ? 'Revealing…' : 'Reveal hint'}
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="link" onClick={() => setConfirming(true)} disabled={disabled}>
            Need a hint?{' '}
            <span className="muted">
              ({revealed.length + 1}/{hints.length})
            </span>
          </button>
        ))}
    </section>
  )
}
